/** VTpass disco IDs — logo paths and fallback colors (from Otobill assets). */
export const DISCO_IDS = [
  'abuja-electric',
  'kaduna-electric',
  'aba-electric',
  'benin-electric',
  'enugu-electric',
  'eko-electric',
  'ibadan-electric',
  'ikeja-electric',
  'jos-electric',
  'kano-electric',
  'portharcourt-electric',
  'yola-electric',
] as const;

export type DiscoId = (typeof DISCO_IDS)[number];

export const DISCO_ABBREVIATIONS: Record<DiscoId, string> = {
  'abuja-electric': 'AEDC',
  'kaduna-electric': 'KAEDCO',
  'aba-electric': 'ABEDC',
  'benin-electric': 'BEDC',
  'enugu-electric': 'EEDC',
  'eko-electric': 'EKEDC',
  'ibadan-electric': 'IBEDC',
  'ikeja-electric': 'IKEDC',
  'jos-electric': 'JED',
  'kano-electric': 'KEDCO',
  'portharcourt-electric': 'PHED',
  'yola-electric': 'YEDC',
};

const DISCO_LOGO_BY_ID: Record<DiscoId, string> = {
  'abuja-electric': '/discos/aedc.png',
  'kaduna-electric': '/discos/kedc.jpeg',
  'aba-electric': '/discos/aba.png',
  'benin-electric': '/discos/bedc.jpeg',
  'enugu-electric': '/discos/enuguedc.jpeg',
  'eko-electric': '/discos/ekedc.png',
  'ibadan-electric': '/discos/ibedc.png',
  'ikeja-electric': '/discos/iedc.png',
  'jos-electric': '/discos/jedc.jpg',
  'kano-electric': '/discos/kedco.jpeg',
  'portharcourt-electric': '/discos/phed.jpeg',
  'yola-electric': '/discos/yedc.jpeg',
};

const DISCO_FALLBACK_COLORS: Record<DiscoId, string> = {
  'abuja-electric': 'bg-red-600',
  'kaduna-electric': 'bg-yellow-600',
  'aba-electric': 'bg-emerald-600',
  'benin-electric': 'bg-green-700',
  'enugu-electric': 'bg-rose-600',
  'eko-electric': 'bg-blue-600',
  'ibadan-electric': 'bg-indigo-600',
  'ikeja-electric': 'bg-orange-500',
  'jos-electric': 'bg-amber-600',
  'kano-electric': 'bg-purple-600',
  'portharcourt-electric': 'bg-teal-600',
  'yola-electric': 'bg-cyan-600',
};

export function getDiscoLogoPath(discoId: string): string | null {
  return DISCO_LOGO_BY_ID[discoId as DiscoId] ?? null;
}

export function getDiscoAbbreviation(discoId: string, discoName: string): string {
  return DISCO_ABBREVIATIONS[discoId as DiscoId] ?? discoName;
}

export function getDiscoFallbackColor(discoId: string): string {
  return DISCO_FALLBACK_COLORS[discoId as DiscoId] ?? 'bg-gray-500';
}

const CABLE_COLORS: Record<string, string> = {
  dstv: 'bg-blue-600',
  gotv: 'bg-rose-600',
  startimes: 'bg-orange-500',
  showmax: 'bg-purple-600',
};

const CABLE_LOGOS: Record<string, string> = {
  dstv: '/cable/dstv.svg',
  gotv: '/cable/gotv.png',
  startimes: '/cable/startimes.png',
};

export function getCableProviderLogo(serviceId: string): string | null {
  const key = serviceId.toLowerCase();
  return CABLE_LOGOS[key] ?? null;
}

export function getCableProviderStyle(serviceId: string): { color: string; label: string } {
  const key = serviceId.toLowerCase();
  return {
    color: CABLE_COLORS[key] ?? 'bg-gray-600',
    label: key.toUpperCase().slice(0, 3) || '?',
  };
}

const EXAM_LOGOS: Record<string, string> = {
  waec: '/waec.jpeg',
  neco: '/neco.png',
  nabteb: '/nabteb.png',
  jamb: '/jamb.jpeg',
};

const EXAM_COLORS: Record<string, string> = {
  waec: 'bg-blue-700',
  neco: 'bg-emerald-700',
  nabteb: 'bg-amber-700',
  jamb: 'bg-green-700',
};

export function getExamProviderLogo(serviceId: string, name?: string): string | null {
  const key = (serviceId || name || '').toLowerCase();
  if (EXAM_LOGOS[key]) return EXAM_LOGOS[key];
  if (key.includes('nabteb')) return EXAM_LOGOS.nabteb;
  if (key.includes('neco')) return EXAM_LOGOS.neco;
  if (key.includes('jamb')) return EXAM_LOGOS.jamb;
  if (key.includes('waec')) return EXAM_LOGOS.waec;
  return null;
}

export function getExamProviderColor(serviceId: string): string {
  const key = serviceId.toLowerCase();
  if (EXAM_COLORS[key]) return EXAM_COLORS[key];
  if (key.includes('nabteb')) return EXAM_COLORS.nabteb;
  if (key.includes('neco')) return EXAM_COLORS.neco;
  if (key.includes('jamb')) return EXAM_COLORS.jamb;
  if (key.includes('waec')) return EXAM_COLORS.waec;
  return 'bg-gray-600';
}
